import { Stack, Typography } from '@mui/material';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { useUpdatePersonalDetailsDictionary } from 'Hooks/useUpdatePersonalDetailsDictionary';
import { useFormContext } from 'react-hook-form';
import { AutoCompleteInput } from '../../../../../../Components/HookFormInputs/AutoCompleteInput';
import { ToggleButtons } from 'Components/HookFormInputs/ToggleButtons';
import { TextInput } from 'Components/HookFormInputs/TextInput';
import { sanitizeNumbersLength } from 'Utils/sanitizeNumbers';
import { CustomFieldset } from 'Components';
import { sanitizeStringLength } from 'Utils';
import { sanitizeLettersNumbersAndSpacialChars } from 'Utils/sanitizeLettersNumbersAndSpacialChars';

interface IMailAddress {
  isReset: React.MutableRefObject<boolean>;
}

export const MailAddress = ({ isReset }: IMailAddress) => {
  const {
    register,
    formState: { errors, defaultValues },
    watch,
  } = useFormContext();

  const dictionary = useUpdatePersonalDetailsDictionary().address.address;
  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const isMailBox = watch('addresses.1.isMailBox');
  const isDesktop = useIsDesktop();

  return (
    <>
      <fieldset style={{ marginBottom: '20px' }}>
        <Typography fontSize='18px' fontWeight={400} margin='20px auto 10px 0'>
          {dictionary.mailAddressOrMailBox}
        </Typography>
        <legend>{dictionary.mailAddressOrMailBox}</legend>
        <ToggleButtons
          option1={{
            id: false,
            value: dictionary.title,
          }}
          option2={{
            id: true,
            value: dictionary.mailBoxInputLabel4,
          }}
          registerName='addresses.1.isMailBox'
        />
      </fieldset>
      <CustomFieldset>
        <Typography fontSize='18px' fontWeight={400} mb='-10px'>
          {isMailBox ? dictionary.mailBox : dictionary.mailAddress}
        </Typography>
        <legend>
          {isMailBox ? dictionary.mailBox : dictionary.mailAddress}
        </legend>
        <AutoCompleteInput
          name='addresses.1.city'
          label={dictionary.inputLabel1}
          options={
            generalOptions?.cities?.map((city) => ({
              id: city.id,
              value: city.value,
            })) || []
          }
          defaultValue={generalOptions?.cities?.find(
            (city) => city.id === defaultValues?.[1]?.city
          )}
          isReset={isReset}
        />
        {!isMailBox && (
          <TextInput
            label={dictionary.inputLabel2}
            register={register('addresses.1.street', {
              onChange: (e) => sanitizeStringLength(e, 50),
            })}
            errors={(errors as any)?.addresses?.[1]?.street}
            shrink={(watch('addresses.1.street') || '')?.length > 0}
          />
        )}
        {!isMailBox && (
          <Stack
            flexDirection='row'
            justifyContent='space-between'
            gap={isDesktop ? '20px' : '16px'}
          >
            <TextInput
              label={dictionary.inputLabel3}
              register={register('addresses.1.houseNum', {
                onChange: (e) => sanitizeNumbersLength(e, 6),
              })}
              errors={(errors as any)?.addresses?.[1]?.houseNum}
              shrink={(watch('addresses.1.houseNum') || '')?.length > 0}
              isNumbersOnly={true}
            />
            <TextInput
              label={dictionary.inputLabel4}
              register={register('addresses.1.apartmentNum', {
                onChange: (e) => sanitizeNumbersLength(e, 6),
              })}
              errors={(errors as any)?.addresses?.[1]?.apartmentNum}
              shrink={(watch('addresses.1.apartmentNum') || '')?.length > 0}
              isNumbersOnly={true}
            />
          </Stack>
        )}
        {isMailBox && (
          <TextInput
            label={dictionary.mailBoxInputLabel4}
            register={register('addresses.1.mailBox', {
              onChange: (e) => sanitizeLettersNumbersAndSpacialChars(e, 20),
            })}
            errors={(errors as any)?.addresses?.[1]?.mailBox}
            shrink={(watch('addresses.1.mailBox') || '')?.length > 0}
          />
        )}
        <TextInput
          label={dictionary.inputLabel5}
          register={register('addresses.1.zipCode', {
            onChange: (e) => sanitizeNumbersLength(e, 7),
          })}
          errors={(errors as any)?.addresses?.[1]?.zipCode}
          shrink={(watch('addresses.1.zipCode') || '')?.length > 0}
          isNumbersOnly={true}
        />
      </CustomFieldset>
    </>
  );
};
